import { Button, buttonVariants } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type OptionDTO } from 'types/surveys';

export const RadioButtons = ({
  options,
  onSaveResponse,
}: {
  options: OptionDTO[];
  onSaveResponse: (option: OptionDTO) => void;
}): React.ReactElement => {
  const [selectedOption, setSelectedOption] = useState<OptionDTO | null>(null);
  const { t } = useTranslation(LOCIZE_NS.HOME);

  const handleOnSaveResponse = (): void => {
    if (selectedOption) {
      onSaveResponse(selectedOption);
    }
  };

  return (
    <div className="flex flex-col am-lg:gap-18 gap-8 items-end">
      <div
        className={cn(
          'flex flex-col relative w-fit self-start mt-4 am-lg:mt-6 am-lg:ml-4',
          !selectedOption && 'am-lg:mb-10 mb-0'
        )}
      >
        <span className="text-sm text-denim-500 leading-4 mb-6 am-lg:hidden">
          {t('NPSSurvey.Step1.subtitle')}
        </span>
        <div className="flex gap-2 items-center flex-wrap">
          {options.map(option => (
            <div key={option.id}>
              <input
                type="radio"
                id={option.id}
                name={option.id}
                value={option.id}
                className="sr-only peer"
                checked={selectedOption?.id === option.id}
                onChange={() => setSelectedOption(option)}
              />
              <label
                htmlFor={option.id}
                className={cn(
                  buttonVariants({ variant: 'terciary', size: 'md' }),
                  'am-lg:size-12 size-10 text-base font-["Poppins"] font-medium leading-5 p-4 border border-teal-300 rounded-full',
                  'hover:bg-plum-100 hover:border-transparent',
                  'peer-checked:bg-mint-100 peer-checked:border-teal-500',
                  'hover:peer-checked:bg-mint-100 hover:peer-checked:border-teal-500'
                )}
              >
                {option.emoji && <span>{option.emoji}</span>}
                <span className="text-sm text-center">{option.text}</span>
              </label>
            </div>
          ))}
        </div>
        <span className="absolute -left-5 top-14 text-sm text-denim-500 leading-4 hidden am-lg:block">
          {t('NPSSurvey.Step1.legend.min')}
        </span>
        <span className="absolute -right-5 top-14 text-sm text-denim-500 leading-4 hidden am-lg:block">
          {t('NPSSurvey.Step1.legend.max')}
        </span>
      </div>
      {selectedOption && (
        <Button
          variant="primary"
          size="lg"
          icon="trailing"
          className="am-lg:w-fit"
          onClick={handleOnSaveResponse}
        >
          {t('Common.next', { ns: LOCIZE_NS.COMMON })}
          <ChevronRight className="size-6" />
        </Button>
      )}
    </div>
  );
};
